import { Component, OnInit, OnDestroy, ChangeDetectionStrategy, inject } from '@angular/core';
import { BundlesRepositoryService, ConfigurationState, Lien, Order, OrderManagerService, OrderMessagesComponent, OrderRepositoryService, OrderStateRepositoryService, SelectOption, eOrderStatusType, eOrderType, eOrderTypeGroup } from 'reg-hub-common';
import { ActivatedRoute, Router } from '@angular/router';
import { MatDialog } from '@angular/material/dialog';
import { ConfirmSubmitComponent } from '../confirm-submit/confirm-submit.component';
import { takeUntil } from 'rxjs/operators';
import { Subject, timer } from 'rxjs';
//import { ResultsPollerService } from '../../services/results-poller/results-poller.service';
import { ConfirmDeleteComponent } from '../confirm-delete/confirm-delete.component';
import { ConfirmCancelComponent } from '../confirm-cancel/confirm-cancel.component';
import { ConfirmHistoricalComponent } from '../confirm-historical/confirm-historical.component';
import { NavigationHistoryService } from '../../services/navigation-history-service/navigation-history.service';
import { PrintDataService } from '../../services/print-data/print-data.service';
import { DuplicateInComponent } from '../duplicate-in/duplicate-in.component';
import { OrdersHubService } from '../../services/hubs/orders-hub/orders-hub.service';
import { AddToBundleComponent } from '../add-to-bundle/add-to-bundle.component';
import { TranslocoService } from '@jsverse/transloco';

@Component({
  selector: 'app-new-registration',
  templateUrl: './new-registration.component.html',
  styleUrls: ['./new-registration.component.css'],
  changeDetection: ChangeDetectionStrategy.Eager,
  standalone: false
})
export class NewRegistrationComponent implements OnInit, OnDestroy {
  private readonly transloco = inject(TranslocoService);
  private unsubscribe$ = new Subject<void>();

  orderId: string = "";
  order: Order = {
    referenceNumber: "",
    country: "CA",
    jurisdiction: "",
    orderTypeID: eOrderType.notSet,
    lien: {} as Lien
  } as Order;

  parentOrder: Order = {
    referenceNumber: "",
    country: "CA",
    jurisdiction: "",
    orderTypeID: eOrderType.notSet,
    lien: {} as Lien
  } as Order;

  orderTypeGroupID: eOrderTypeGroup = eOrderTypeGroup.PPSARegistration;
  isLoadingOrder: boolean = false;
  finalStepIsSelected: boolean = false;
  showOrderFees: boolean = false;
  private bundleOptions: SelectOption[] = [];

  constructor(private activatedRoute: ActivatedRoute,
    private orderManager: OrderManagerService,
    private orderRepo: OrderRepositoryService,
    private orderStateRepo: OrderStateRepositoryService,
    private dialog: MatDialog,
    private router: Router,
    private ordersHub: OrdersHubService,
    private navigationService: NavigationHistoryService,
    private printDataService: PrintDataService,
    private configurationState: ConfigurationState,
    private bundlesRepo: BundlesRepositoryService
  ) { }

  ngOnInit(): void {
    this.orderManager.currentOrder
      .pipe(takeUntil(this.unsubscribe$))
      .subscribe(order => this.order = order);

    this.orderManager.parentOrder
      .pipe(takeUntil(this.unsubscribe$))
      .subscribe(parentOrder => this.parentOrder = parentOrder);

    this.shouldShowOrderSummary();

    this.activatedRoute.paramMap.subscribe(params => {
      this.orderId = params.get('orderID')!;
      if (this.orderId) {
        // If we have an orderID, load the associated order and stepper
        this.getOrder(this.orderId);
      } else {
        // if we don't have an orderID, load base stepper
        this.resetOrder();
        this.orderManager.updateOrder(this.order);
      }
    });
  }

  ngOnDestroy(): void {
    // Signal all subscriptions to complete
    this.unsubscribe$.next();
    this.unsubscribe$.complete();
  }

  private getOrder = (orderID: string) => {
    // this wil call api/v1/orders/id
    this.isLoadingOrder = true;
    this.orderRepo.getOrder('orders', orderID)
      .subscribe(order => {
        this.isLoadingOrder = false;
        this.order = order as Order;
        this.orderManager.updateOrder(this.order);
        this.getBundles();
      });
  };

  messageCount() {
    let length = this.order?.orderMessages?.filter((u) => u.isCustomerVisible && !u.isReadByCustomer).length ?? 0;
    return length > 0 ? length.toString() : "";
  }

  shouldShowOrderMessages(): boolean {
    return this.order.orderStatusTypeID == 'Invalid'
      || this.messageCount() != "";
  }

  shouldShowOrderSummary() {
    this.configurationState.hasOrderFeeVisibility().then(show => {
      this.showOrderFees = show;
    });
  }

  openOrderMessages() {
    this.dialog.open(OrderMessagesComponent, {
      data: { order: this.order, customer: true },
      minWidth: '800px'
    });
  }

  submissionConfirmation(order: Order) {
    const isHistorical = this.order?.orderStatusTypeID === eOrderStatusType.historicalDraft;
    const isBasicDischarge = this.order?.orderTypeID === eOrderType.basicDischarge;

    const dialogRef = isHistorical ?
      this.dialog.open(ConfirmHistoricalComponent) :
      this.dialog.open(ConfirmSubmitComponent, { data: { customMessage: isBasicDischarge ? this.transloco.translate('orders.confirmTotalDischarge') : undefined } });

    dialogRef.afterClosed().subscribe(result => {
      if (result.submit === true) {
        this.submitOrder();

        if (!isHistorical) {
          this.ordersHub.connect().subscribe(() => this.ordersHub.watchOrder(order));
        }
      }
    });
  }

  submitOrder() {
    //Limit flashing of order spinner
    var timerSubscription = timer(250).subscribe(() => this.isLoadingOrder = true);

    const pendingStatus = {
      newStatus: (this.order?.orderStatusTypeID == "HistoricalDraft") ? "Historical" : "Pending",
      status: "",
      message: "Submitted from Reg Hub Client"
    };

    this.orderStateRepo.updateOrderState(this.order.id, pendingStatus)
      .subscribe({
        next: () => {
          timerSubscription.unsubscribe();
          this.isLoadingOrder = false;
          this.ordersHub.watchOrder(this.order);
          this.order.orderStatusTypeID = "Pending";
          this.orderManager.updateOrder(this.order);
          this.router.navigate(['orders', this.order.id]);
        },
        error: () => {
          timerSubscription.unsubscribe();
          this.isLoadingOrder = false;
        }
      });
  }

  deletionConfirmation() {
    if (this.order?.orderStatusTypeID == "Draft" ||
      this.order?.orderStatusTypeID == "HistoricalDraft") {
      const dialogRef = this.dialog.open(ConfirmDeleteComponent);

      dialogRef.afterClosed().subscribe(result => {
        if (result) {
          // deletion is confirmed, delet order
          this.deleteOrder();
        }
      });
    }
  }

  deleteOrder() {
    const orderID = this.order.id;

    this.orderRepo.deleteOrder("Orders/" + orderID)
      .subscribe({
        next: () => {
          let previousUrl = this.navigationService.getPreviousUrl();

          if (previousUrl?.toString()?.includes(orderID)) {
            previousUrl = this.navigationService.getPreviousUrl(1);
          }

          this.router.navigateByUrl(previousUrl);
        },
        error: (error) => {
          console.log(error);
        }
      });
  }

  cancelConfirmation() {
    const dialogRef = this.dialog.open(ConfirmCancelComponent);

    dialogRef.afterClosed().subscribe(result => {
      if (result) {
        // deletion is confirmed, delet order
        this.cancelOrder();
      }
    });
  }

  cancelOrder() {
    var timerSubscription = timer(250).subscribe(() => this.isLoadingOrder = true);

    const pendingStatus = {
      newStatus: "Cancelled",
      status: "",
      message: "Cancelled from Reg Hub Client"
    };

    this.orderStateRepo.updateOrderState(this.order.id, pendingStatus)
      .subscribe({
        next: (result) => {
          timerSubscription.unsubscribe();
          this.isLoadingOrder = false;

          if (!result) {
            //this.resultPoller.startPolling(this.order.id);
            this.order.orderStatusTypeID = "Cancelled";
            this.orderManager.updateOrder(this.order);
            this.router.navigate(['orders', this.order.id]);
          }
        },
        error: () => {
          timerSubscription.unsubscribe();
          this.isLoadingOrder = false;
        }
      });
  }

  isDeleteable() {
    return this.order.orderStatusTypeID === eOrderStatusType.draft || this.order.orderStatusTypeID === eOrderStatusType.historicalDraft;
  }

  print() {
    this.printDataService.printOrder();
  }

  showPrintButton() {
    return this.printDataService.shouldShowPrintOrderButton(this.order);
  }

  private resetOrder() {
    this.order = {
      referenceNumber: "",
      country: "CA",
      jurisdiction: "",
      orderTypeID: eOrderType.notSet,
      lien: {} as Lien
    } as Order;
  }

  showDuplicate(): boolean {
    return (this.order.orderType.orderTypeGroup.id === eOrderTypeGroup.PPSARegistration ||
      this.order.orderType.orderTypeGroup.id === eOrderTypeGroup.PPSAChangeManagement) &&
      this.order.orderStatusTypeID !== eOrderStatusType.historical &&
      this.order.orderStatusTypeID !== eOrderStatusType.historicalDraft &&
      this.order.orderTypeID !== eOrderType.assignment &&
      this.order.orderTypeID !== eOrderType.reregistration;
  }

  duplicate() {
    const dialogRef = this.dialog.open(DuplicateInComponent, {
      data: this.order,
      width: '500px'
    });

    dialogRef.afterClosed().subscribe(result => {
      if (result.event === 'submitted') {
        this.isLoadingOrder = true;
      }
    });
  }

  getBundles() {
    if (this.order.orderGroup) {
      this.bundlesRepo.getBundlesAsSelectOptions(this.order.orderGroup!.corporationID).subscribe(bundles => {
        this.bundleOptions = bundles;
      });
    }
  }

  getAvailableBundles(): SelectOption[] {
    var orderBundles = this.order.orderGroup?.bundles?.map(bundle => bundle.id) ?? [];

    return this.bundleOptions.filter(opt => !orderBundles.includes(opt.value as string));
  }

  showBundlesButton() {
    let availableBundles = this.getAvailableBundles();

    return availableBundles.length > 0;
  }

  addToBundle() {
    const dialogRef = this.dialog.open(AddToBundleComponent, {
      data: { order: this.order, bundles: this.getAvailableBundles() },
      minWidth: '500px'
    });
  }
}
